'use client';

import { useState } from 'react';

import type { Cart } from '@/entities/cart';
import { ProductMedia } from '@/entities/product';
import { formatMoney, formatNumber, rub, type Money } from '@/shared/lib/money';
import { formatCount, GOODS_FORMS } from '@/shared/lib/plural';
import { Icon } from '@/shared/ui/icon';

import styles from './checkout-flow.module.scss';

/** Dark order summary (05B): recalculated with the chosen delivery. Collapsible on phones. */
export function OrderSummary({ cart, deliveryPrice }: { cart: Cart; deliveryPrice: Money | null }) {
  const [open, setOpen] = useState(false);
  const total = rub(cart.total.amountMinor + (deliveryPrice?.amountMinor ?? 0));
  const deliveryText = cart.delivery.isFree
    ? 'Бесплатно'
    : deliveryPrice
      ? formatMoney(deliveryPrice)
      : 'Рассчитаем при оформлении';
  return (
    <aside className={styles.summary} aria-label="Ваш заказ" data-open={open || undefined}>
      <button
        type="button"
        className={styles['summary-toggle']}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span>Ваш заказ · {formatCount(cart.itemsCount, GOODS_FORMS)}</span>
        <span className={styles['summary-toggle-total']}>
          {formatMoney(total)} <Icon name="chevron-down" size={18} strokeWidth={1.4} />
        </span>
      </button>
      <div className={styles['summary-body']}>
        <span className={styles['summary-count']}>{formatCount(cart.itemsCount, GOODS_FORMS)}</span>
        <ul className={styles.thumbs}>
          {cart.lines.slice(0, 6).map((line) => (
            <li key={line.lineId}>
              <ProductMedia
                media={line.media}
                fallbackLabel=""
                sizes="64px"
                className={styles.thumb}
                placeholderSub=""
                placeholderStyle="plain"
                sketchSize="68.75%"
                position="center top"
              />
            </li>
          ))}
        </ul>
        <dl className={styles.sums}>
          <div className={styles.sum}>
            <dt>Товары</dt>
            <dd>{formatMoney(cart.subtotal)}</dd>
          </div>
          {cart.discountTotal.amountMinor > 0 && (
            <div className={styles.sum}>
              <dt>Скидка</dt>
              <dd>−{formatMoney(cart.discountTotal)}</dd>
            </div>
          )}
          <div className={styles.sum}>
            <dt>Доставка</dt>
            <dd>{deliveryText}</dd>
          </div>
          <div className={styles.sum}>
            <dt>Бонусы к начислению</dt>
            <dd>{formatNumber(cart.bonusAccrual)}</dd>
          </div>
        </dl>
        <div className={styles.total}>
          <span className={styles['total-label']}>Итого</span>
          <span className={styles['total-value']}>{formatMoney(total)}</span>
        </div>
        {cart.hasPreorder && (
          <p className={styles.note}>
            <Icon name="clock" size={15} strokeWidth={1.4} />
            <span>
              В заказе есть товар по предзаказу. Все позиции отправим вместе, когда он поступит.
            </span>
          </p>
        )}
      </div>
    </aside>
  );
}
